
import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Cliente } from '@shared/types';
import { useUser } from '@/contexts/UserContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2Icon, SaveIcon, BanIcon } from '@/components/Icons';
import { Building2, AlertCircle, Search, ChevronDown, ChevronUp, CheckCircle2 } from 'lucide-react';
import { isValidNifCif } from '@/utils/validators';
import { toast } from 'sonner';

interface AdministracionFACe {
    codigo: string;
    nombre: string;
}

interface UnidadDIR3 {
    codigo: string;
    nombre: string;
}

interface RelacionOGUTOC {
    organoGestor: UnidadDIR3;
    unidadTramitadora: UnidadDIR3;
    oficinaContable: UnidadDIR3;
}

// Validación de código DIR3 (flexible para aceptar diferentes formatos reales)
const isValidDIR3 = (codigo: string): boolean => {
    if (!codigo || codigo.trim().length === 0) return false;

    // Eliminar espacios y convertir a mayúsculas
    const codigoLimpio = codigo.trim().replace(/\s+/g, '').toUpperCase();

    // Aceptar diferentes formatos comunes de DIR3:
    // - 2 letras + 8-10 dígitos (formato estándar): LA0001234567
    // - Solo dígitos (formato corto): 0001234
    // - Letras + dígitos (formato mixto): A00000000
    // - Mínimo 7 caracteres, máximo 12
    if (codigoLimpio.length < 7 || codigoLimpio.length > 12) return false;

    // Debe contener al menos algunos dígitos
    return /\d/.test(codigoLimpio);
};

export default function ClienteForm() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { fetchWithAuth } = useUser();
    const [cliente, setCliente] = useState<Partial<Cliente>>({
        nombre: '',
        email: '',
        nif: '',
        telefono: '',
        direccion: '',
        codigoPostal: '',
        ciudad: '',
        provincia: '',
        pais: 'España',
        esAdministracionPublica: false
    });
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // DIR3 directory search state
    const [dir3Open, setDir3Open] = useState(false);
    const [administraciones, setAdministraciones] = useState<AdministracionFACe[]>([]);
    const [adminFilter, setAdminFilter] = useState('');
    const [loadingAdmin, setLoadingAdmin] = useState(false);
    const [selectedAdmin, setSelectedAdmin] = useState<string | null>(null);
    const [unidades, setUnidades] = useState<RelacionOGUTOC[]>([]);
    const [loadingUnidades, setLoadingUnidades] = useState(false);

    useEffect(() => {
        if (id) {
            setIsLoading(true);
            fetchWithAuth(`/api/clientes/${id}`)
                .then(res => {
                    if (!res.ok) throw new Error('Cliente no encontrado');
                    return res.json();
                })
                .then(data => setCliente(data))
                .catch(error => {
                    toast.error('Error al cargar cliente', { description: error.message });
                    navigate('/clientes');
                })
                .finally(() => setIsLoading(false));
        }
    }, [id, navigate, fetchWithAuth]);

    const fetchAdministraciones = useCallback(async () => {
        if (administraciones.length > 0) return;
        setLoadingAdmin(true);
        try {
            const res = await fetchWithAuth('/api/face/dir3/administraciones');
            if (res.ok) {
                const data: AdministracionFACe[] = await res.json();
                setAdministraciones(data);
            } else {
                toast.error('Error al cargar administraciones');
            }
        } catch {
            toast.error('No se pudo conectar con el directorio FACe');
        } finally {
            setLoadingAdmin(false);
        }
    }, [fetchWithAuth, administraciones.length]);

    const fetchUnidades = useCallback(async (codigoAdmin: string) => {
        setLoadingUnidades(true);
        setUnidades([]);
        try {
            const res = await fetchWithAuth(`/api/face/dir3/unidades/${encodeURIComponent(codigoAdmin)}`);
            if (res.ok) {
                const data: RelacionOGUTOC[] = await res.json();
                setUnidades(data);
                if (data.length === 0) {
                    toast.info('Esta administración no tiene relaciones OG-UT-OC registradas');
                }
            }
        } catch {
            toast.error('Error al cargar unidades DIR3');
        } finally {
            setLoadingUnidades(false);
        }
    }, [fetchWithAuth]);

    const handleSelectRelacion = (relacion: RelacionOGUTOC) => {
        setCliente(prev => ({
            ...prev,
            oficinaContable: relacion.oficinaContable.codigo,
            organoGestor: relacion.organoGestor.codigo,
            unidadTramitadora: relacion.unidadTramitadora.codigo,
            nombreOficinaContable: relacion.oficinaContable.nombre,
            nombreOrganoGestor: relacion.organoGestor.nombre,
            nombreUnidadTramitadora: relacion.unidadTramitadora.nombre,
        }));
        toast.success('Códigos DIR3 aplicados');
    };

    const handleOpenDir3Search = () => {
        setDir3Open(prev => !prev);
        if (!dir3Open) {
            fetchAdministraciones();
        }
    };

    const handleSelectAdmin = (codigoAdmin: string) => {
        setSelectedAdmin(codigoAdmin);
        fetchUnidades(codigoAdmin);
    };

    const filteredAdmin = adminFilter
        ? administraciones.filter(a =>
            a.nombre.toLowerCase().includes(adminFilter.toLowerCase()) ||
            a.codigo.toLowerCase().includes(adminFilter.toLowerCase())
        )
        : administraciones;

    const handleChange = (field: keyof Cliente, value: string) => {
        setCliente(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        if (!cliente.nombre?.trim()) {
            toast.warning('Error de validación', { description: 'El nombre es obligatorio.' });
            return;
        }
        if (cliente.nif && !isValidNifCif(cliente.nif)) {
            toast.warning('Error de validación', { description: 'El NIF/CIF/NIE introducido no es válido.' });
            return;
        }

        // Validar códigos DIR3 si es Administración Pública
        if (cliente.esAdministracionPublica) {
            if (!cliente.oficinaContable || !cliente.organoGestor || !cliente.unidadTramitadora) {
                toast.warning('Error de validación', {
                    description: 'Para Administraciones Públicas son obligatorios los 3 códigos DIR3.'
                });
                return;
            }

            if (!isValidDIR3(cliente.oficinaContable)) {
                toast.warning('Error de validación', {
                    description: 'El código de Oficina Contable no es válido. Debe tener entre 7-12 caracteres alfanuméricos.'
                });
                return;
            }

            if (!isValidDIR3(cliente.organoGestor)) {
                toast.warning('Error de validación', {
                    description: 'El código de Órgano Gestor no es válido. Debe tener entre 7-12 caracteres alfanuméricos.'
                });
                return;
            }

            if (!isValidDIR3(cliente.unidadTramitadora)) {
                toast.warning('Error de validación', {
                    description: 'El código de Unidad Tramitadora no es válido. Debe tener entre 7-12 caracteres alfanuméricos.'
                });
                return;
            }
        }

        setIsSaving(true);
        try {
            const response = await fetchWithAuth(id ? `/api/clientes/${id}` : '/api/clientes', {
                method: id ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(cliente),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Error al guardar el cliente');
            }
            
            toast.success(`Cliente ${id ? 'actualizado' : 'creado'} con éxito`);
            navigate('/clientes');

        } catch (error) {
            toast.error('Error al guardar', { description: error instanceof Error ? error.message : 'Ocurrió un error inesperado' });
        } finally {
            setIsSaving(false);
        }
    };
    
    if (isLoading && id) return <div className="flex justify-center items-center h-64"><Loader2Icon className="animate-spin h-8 w-8 text-primary" /></div>

    return (
        <div className="max-w-4xl mx-auto space-y-8">
            <div className="flex justify-between items-center">
                <h1 className="text-3xl font-bold tracking-tighter">{id ? 'Editar Cliente' : 'Nuevo Cliente'}</h1>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => navigate('/clientes')} disabled={isSaving}><BanIcon className="mr-2" />Cancelar</Button>
                    <Button onClick={handleSave} disabled={isSaving}>
                        {isSaving ? <><Loader2Icon className="mr-2 h-4 w-4 animate-spin"/> Guardando...</> : <><SaveIcon className="mr-2" />Guardar</>}
                    </Button>
                </div>
            </div>

            <Card>
                <CardHeader><CardTitle>Información del Cliente</CardTitle></CardHeader>
                <CardContent className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <Label htmlFor="nombre">Nombre o Razón Social</Label>
                            <Input id="nombre" value={cliente.nombre ?? ''} onChange={(e) => handleChange('nombre', e.target.value)} placeholder="Ej. Mi Empresa S.L." />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="nif">NIF/CIF/NIE</Label>
                            <Input id="nif" value={cliente.nif ?? ''} onChange={(e) => handleChange('nif', e.target.value)} placeholder="Ej. B12345678" />
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email de Contacto</Label>
                            <Input id="email" type="email" value={cliente.email ?? ''} onChange={(e) => handleChange('email', e.target.value)} placeholder="Ej. contacto@empresa.com" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="telefono">Teléfono</Label>
                            <Input id="telefono" value={cliente.telefono ?? ''} onChange={(e) => handleChange('telefono', e.target.value)} placeholder="Ej. 912 345 678" />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="personaContacto">Persona de Contacto</Label>
                        <Input id="personaContacto" value={cliente.personaContacto ?? ''} onChange={(e) => handleChange('personaContacto', e.target.value)} placeholder="Ej. Juan Pérez" />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="direccion">Dirección</Label>
                        <Input id="direccion" value={cliente.direccion ?? ''} onChange={(e) => handleChange('direccion', e.target.value)} placeholder="Ej. Calle Mayor, 1"/>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                        <div className="space-y-2">
                            <Label htmlFor="codigoPostal">Código Postal</Label>
                            <Input id="codigoPostal" value={cliente.codigoPostal ?? ''} onChange={(e) => handleChange('codigoPostal', e.target.value)} placeholder="Ej. 28013" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="ciudad">Municipio</Label>
                            <Input id="ciudad" value={cliente.ciudad ?? ''} onChange={(e) => handleChange('ciudad', e.target.value)} placeholder="Ej. Madrid" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="provincia">Provincia</Label>
                            <Input id="provincia" value={cliente.provincia ?? ''} onChange={(e) => handleChange('provincia', e.target.value)} placeholder="Ej. Madrid" />
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Building2 className="h-5 w-5" />
                        Administración Pública y FACe
                    </CardTitle>
                    <CardDescription>
                        Configuración para facturación a organismos públicos
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    {/* Toggle Administración Pública */}
                    <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/50">
                        <div className="space-y-0.5">
                            <Label htmlFor="esAdministracionPublica" className="text-base">
                                Es Administración Pública
                            </Label>
                            <p className="text-sm text-muted-foreground">
                                Activa esta opción si este cliente es una AAPP que requiere envío a FACe
                            </p>
                        </div>
                        <Switch
                            id="esAdministracionPublica"
                            checked={cliente.esAdministracionPublica ?? false}
                            onCheckedChange={(checked) =>
                                setCliente(prev => ({ ...prev, esAdministracionPublica: checked }))
                            }
                        />
                    </div>

                    {/* Códigos DIR3 */}
                    {cliente.esAdministracionPublica && (
                        <>
                            <Alert>
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription>
                                    Los códigos DIR3 son <strong>obligatorios</strong> para Administraciones Públicas.
                                    Usa el buscador del directorio FACe o introduce los códigos manualmente.
                                </AlertDescription>
                            </Alert>

                            {/* Buscador directorio FACe */}
                            <div className="border rounded-lg overflow-hidden">
                                <button
                                    type="button"
                                    onClick={handleOpenDir3Search}
                                    className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-colors text-left"
                                >
                                    <div className="flex items-center gap-2">
                                        <Search className="h-4 w-4 text-primary" />
                                        <span className="font-medium text-sm">Buscar en directorio FACe</span>
                                    </div>
                                    {dir3Open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                </button>

                                {dir3Open && (
                                    <div className="p-4 space-y-4 border-t">
                                        {/* Filtro de administraciones */}
                                        <div className="space-y-2">
                                            <Label className="text-sm">Administración</Label>
                                            <Input
                                                placeholder="Filtra por nombre o código..."
                                                value={adminFilter}
                                                onChange={(e) => setAdminFilter(e.target.value)}
                                                disabled={loadingAdmin}
                                            />
                                        </div>

                                        {loadingAdmin && (
                                            <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                                                <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                                                Cargando administraciones...
                                            </div>
                                        )}

                                        {!loadingAdmin && administraciones.length > 0 && (
                                            <div className="max-h-48 overflow-y-auto border rounded-md divide-y">
                                                {filteredAdmin.slice(0, 50).map(admin => (
                                                    <button
                                                        key={admin.codigo}
                                                        type="button"
                                                        onClick={() => handleSelectAdmin(admin.codigo)}
                                                        className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/50 transition-colors ${
                                                            selectedAdmin === admin.codigo ? 'bg-primary/10 font-medium' : ''
                                                        }`}
                                                    >
                                                        <span className="font-mono text-xs text-muted-foreground mr-2">{admin.codigo}</span>
                                                        {admin.nombre}
                                                    </button>
                                                ))}
                                                {filteredAdmin.length > 50 && (
                                                    <p className="px-3 py-2 text-xs text-muted-foreground text-center">
                                                        Mostrando 50 de {filteredAdmin.length} — refina tu búsqueda
                                                    </p>
                                                )}
                                                {filteredAdmin.length === 0 && (
                                                    <p className="px-3 py-2 text-xs text-muted-foreground text-center">
                                                        No se encontraron administraciones
                                                    </p>
                                                )}
                                            </div>
                                        )}

                                        {/* Relaciones OG-UT-OC */}
                                        {loadingUnidades && (
                                            <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                                                <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                                                Cargando unidades DIR3...
                                            </div>
                                        )}

                                        {!loadingUnidades && unidades.length > 0 && (
                                            <div className="space-y-2">
                                                <Label className="text-sm">Selecciona la relación OG-UT-OC</Label>
                                                <div className="max-h-64 overflow-y-auto border rounded-md divide-y">
                                                    {unidades.map((rel, i) => {
                                                        const isSelected =
                                                            cliente.oficinaContable === rel.oficinaContable.codigo &&
                                                            cliente.organoGestor === rel.organoGestor.codigo &&
                                                            cliente.unidadTramitadora === rel.unidadTramitadora.codigo;
                                                        return (
                                                            <button
                                                                key={`${rel.organoGestor.codigo}-${rel.unidadTramitadora.codigo}-${rel.oficinaContable.codigo}-${i}`}
                                                                type="button"
                                                                onClick={() => handleSelectRelacion(rel)}
                                                                className={`w-full text-left px-3 py-3 text-sm hover:bg-muted/50 transition-colors ${
                                                                    isSelected ? 'bg-green-50 dark:bg-green-950/30 ring-1 ring-green-300' : ''
                                                                }`}
                                                            >
                                                                <div className="grid grid-cols-3 gap-2">
                                                                    <div>
                                                                        <p className="text-xs text-muted-foreground">OC</p>
                                                                        <p className="font-mono text-xs">{rel.oficinaContable.codigo}</p>
                                                                        <p className="text-xs truncate" title={rel.oficinaContable.nombre}>{rel.oficinaContable.nombre}</p>
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-xs text-muted-foreground">OG</p>
                                                                        <p className="font-mono text-xs">{rel.organoGestor.codigo}</p>
                                                                        <p className="text-xs truncate" title={rel.organoGestor.nombre}>{rel.organoGestor.nombre}</p>
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-xs text-muted-foreground">UT</p>
                                                                        <p className="font-mono text-xs">{rel.unidadTramitadora.codigo}</p>
                                                                        <p className="text-xs truncate" title={rel.unidadTramitadora.nombre}>{rel.unidadTramitadora.nombre}</p>
                                                                    </div>
                                                                </div>
                                                                {isSelected && (
                                                                    <div className="flex items-center gap-1 mt-1 text-green-600 dark:text-green-400">
                                                                        <CheckCircle2 className="h-3 w-3" />
                                                                        <span className="text-xs">Seleccionada</span>
                                                                    </div>
                                                                )}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Campos DIR3 (editables manualmente) */}
                            <div className="grid md:grid-cols-3 gap-6">
                                <div className="space-y-2">
                                    <Label htmlFor="oficinaContable" className="flex items-center gap-1">
                                        Oficina Contable <span className="text-red-500">*</span>
                                    </Label>
                                    <Input
                                        id="oficinaContable"
                                        value={cliente.oficinaContable ?? ''}
                                        onChange={(e) => handleChange('oficinaContable', e.target.value.toUpperCase())}
                                        placeholder="Código DIR3"
                                        className={cliente.oficinaContable && !isValidDIR3(cliente.oficinaContable) ? 'border-red-500' : ''}
                                    />
                                    {cliente.nombreOficinaContable && (
                                        <p className="text-xs text-muted-foreground truncate" title={cliente.nombreOficinaContable}>{cliente.nombreOficinaContable}</p>
                                    )}
                                    {cliente.oficinaContable && !isValidDIR3(cliente.oficinaContable) && (
                                        <p className="text-xs text-red-500">Código inválido (7-12 caracteres alfanuméricos)</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="organoGestor" className="flex items-center gap-1">
                                        Órgano Gestor <span className="text-red-500">*</span>
                                    </Label>
                                    <Input
                                        id="organoGestor"
                                        value={cliente.organoGestor ?? ''}
                                        onChange={(e) => handleChange('organoGestor', e.target.value.toUpperCase())}
                                        placeholder="Código DIR3"
                                        className={cliente.organoGestor && !isValidDIR3(cliente.organoGestor) ? 'border-red-500' : ''}
                                    />
                                    {cliente.nombreOrganoGestor && (
                                        <p className="text-xs text-muted-foreground truncate" title={cliente.nombreOrganoGestor}>{cliente.nombreOrganoGestor}</p>
                                    )}
                                    {cliente.organoGestor && !isValidDIR3(cliente.organoGestor) && (
                                        <p className="text-xs text-red-500">Código inválido (7-12 caracteres alfanuméricos)</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="unidadTramitadora" className="flex items-center gap-1">
                                        Unidad Tramitadora <span className="text-red-500">*</span>
                                    </Label>
                                    <Input
                                        id="unidadTramitadora"
                                        value={cliente.unidadTramitadora ?? ''}
                                        onChange={(e) => handleChange('unidadTramitadora', e.target.value.toUpperCase())}
                                        placeholder="Código DIR3"
                                        className={cliente.unidadTramitadora && !isValidDIR3(cliente.unidadTramitadora) ? 'border-red-500' : ''}
                                    />
                                    {cliente.nombreUnidadTramitadora && (
                                        <p className="text-xs text-muted-foreground truncate" title={cliente.nombreUnidadTramitadora}>{cliente.nombreUnidadTramitadora}</p>
                                    )}
                                    {cliente.unidadTramitadora && !isValidDIR3(cliente.unidadTramitadora) && (
                                        <p className="text-xs text-red-500">Código inválido (7-12 caracteres alfanuméricos)</p>
                                    )}
                                </div>
                            </div>

                            <div className="text-xs text-muted-foreground pt-2 border-t">
                                <p>
                                    Consulta los códigos DIR3 oficiales en:{' '}
                                    <a
                                        href="https://dir3.redsara.es/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary hover:underline"
                                    >
                                        dir3.redsara.es
                                    </a>
                                </p>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
